import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, User, Lock, Landmark, Bell, Grid3x3,
  Loader2, Save, Plug, Check, X, Trash2, AlertTriangle, ShieldCheck,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import PatternLock from "@/components/PatternLock";
import MfaSection from "@/components/MfaSection";
import ConnectBankButton from "@/components/ConnectBankButton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";


const NOTIF_LABELS = {
  bill_due_soon: "Bill due in 3 days",
  new_drain_detected: "New bill drain detected",
  weekly_boss: "Weekly Boss Battle ready",
  streak_reminder: "Weekly streak check-in reminder",
  price_hike: "Subscription price hike alert",
  vault_deposit: "Vault deposit confirmations",
  email_newsletter: "Hero Tips email newsletter",
};


export default function Account() {
  const navigate = useNavigate();
  const { refreshMe, logout } = useAuth();

  const [account, setAccount] = useState(null);
  const [banks, setBanks] = useState([]);
  const [plaidStatus, setPlaidStatus] = useState({ configured: false, linked: false, item_count: 0, max_banks: 1, accounts: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");

  // Password
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");

  // App lock dialog state
  const [lockStage, setLockStage] = useState(null); // null | 'current' | 'new' | 'confirm' | 'disable'
  const [firstPattern, setFirstPattern] = useState("");
  const [patternError, setPatternError] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);

  // Delete-account dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/account"),
        api.get("/plaid/status").catch(() => ({ data: { accounts: [] } })),
      ]);
      setAccount(a.data);
      setName(a.data.name || "");
      setEmail(a.data.email || "");
      setPhone(a.data.phone || "");
      setAddress(a.data.address || "");
      setBanks(b.data?.accounts || b.data?.items || []);
      setPlaidStatus({
        configured: !!b.data?.configured,
        linked: !!b.data?.linked,
        item_count: b.data?.item_count || 0,
        max_banks: b.data?.max_banks || 1,
        accounts: b.data?.accounts || [],
      });
    } catch {
      toast.error("Couldn't load your account.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch("/account", { name, email, phone, address });
      setAccount(r.data);
      await refreshMe?.();
      toast.success("Profile saved.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const changePw = async (e) => {
    e?.preventDefault();
    if (newPw.length < 8) return toast.error("New password needs at least 8 characters.");
    setSaving(true);
    try {
      await api.post("/account/change-password", { current_password: currentPw, new_password: newPw });
      setCurrentPw(""); setNewPw("");
      toast.success("Password updated.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Couldn't change password.");
    } finally {
      setSaving(false);
    }
  };

  const toggleNotif = async (key, next) => {
    const merged = { ...(account?.notification_prefs || {}), [key]: next };
    setAccount((a) => ({ ...a, notification_prefs: merged }));
    try {
      await api.patch("/account", { notification_prefs: { [key]: next } });
    } catch {
      toast.error("Couldn't update notification.");
      setAccount((a) => ({ ...a, notification_prefs: { ...merged, [key]: !next } }));
    }
  };

  const reloadBanks = async () => {
    try {
      const b = await api.get("/plaid/status");
      setBanks(b.data?.accounts || b.data?.items || []);
      setPlaidStatus({
        configured: !!b.data?.configured,
        linked: !!b.data?.linked,
        item_count: b.data?.item_count || 0,
        max_banks: b.data?.max_banks || 1,
        accounts: b.data?.accounts || [],
      });
    } catch {
      /* ignore — non-blocking */
    }
  };

  const disconnectBank = async (itemId) => {
    if (!window.confirm("Disconnect this bank? Recurring charges already imported from it stay, but new ones won't sync.")) return;
    try {
      const url = itemId ? `/plaid/disconnect?item_id=${encodeURIComponent(itemId)}` : "/plaid/disconnect";
      await api.delete(url);
      toast.success("Bank disconnected.");
      reloadBanks();
    } catch {
      toast.error("Couldn't disconnect.");
    }
  };

  const [editingBankId, setEditingBankId] = useState(null);
  const [editingNickname, setEditingNickname] = useState("");
  const [savingNickname, setSavingNickname] = useState(false);

  const startEditNickname = (itemId, currentNickname) => {
    setEditingBankId(itemId);
    setEditingNickname(currentNickname || "");
  };
  const cancelEditNickname = () => {
    setEditingBankId(null);
    setEditingNickname("");
  };
  const saveNickname = async (itemId) => {
    setSavingNickname(true);
    try {
      await api.patch(`/plaid/items/${encodeURIComponent(itemId)}`, {
        nickname: editingNickname.trim(),
      });
      toast.success(editingNickname.trim() ? "Nickname saved." : "Nickname cleared.");
      cancelEditNickname();
      reloadBanks();
    } catch {
      toast.error("Couldn't save nickname.");
    } finally {
      setSavingNickname(false);
    }
  };

  const openLockFlow = () => {
    setFirstPattern("");
    setPatternError(false);
    setLockStage(account?.app_lock_enabled ? "disable" : "new");
    setLockDialogOpen(true);
  };

  const handlePatternComplete = async (pattern) => {
    setPatternError(false);
    if (lockStage === "new") {
      setFirstPattern(pattern);
      setLockStage("confirm");
      return;
    }
    if (lockStage === "confirm") {
      if (pattern !== firstPattern) {
        setPatternError(true);
        toast.error("Patterns don't match — try again.");
        setTimeout(() => { setLockStage("new"); setFirstPattern(""); setPatternError(false); }, 600);
        return;
      }
      try {
        const r = await api.post("/account/app-lock", { pattern });
        setAccount(r.data);
        await refreshMe?.();
        toast.success("App lock enabled.");
        setLockDialogOpen(false);
      } catch (err) {
        const d = err.response?.data?.detail;
        toast.error((typeof d === "string" ? d : d?.message) || "Couldn't set lock.");
      }
      return;
    }
    if (lockStage === "disable") {
      try {
        const r = await api.delete("/account/app-lock", { data: { pattern } });
        setAccount(r.data);
        await refreshMe?.();
        toast.success("App lock disabled.");
        setLockDialogOpen(false);
      } catch {
        setPatternError(true);
      }
      return;
    }
  };

  const confirmDelete = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== "DELETE") {
      toast.error("Type DELETE to confirm.");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/account");
      try { window.localStorage.clear(); } catch { /* ignore */ }
      toast.success("Account deleted.", { description: "All your data has been removed." });
      try { await logout(); } catch { /* already logged out server-side */ }
      navigate("/login", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Couldn't delete account. Try again.");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="hero-theme min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#22E07A]" />
      </div>
    );
  }

  return (
    <div className="hero-theme min-h-screen px-5 py-6 pb-20" data-testid="account-page">
      <header className="max-w-md mx-auto flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 rounded-xl text-[#C7DAD3] hover:bg-[#11413B]/60"
          data-testid="back-button"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-black uppercase tracking-wider text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
          Account
        </h1>
      </header>

      <div className="max-w-md mx-auto space-y-6">
        {/* Profile */}
        <section className="ht-card rounded-2xl p-5" data-testid="section-profile">
          <SectionHeader icon={User} title="Profile" />
          <form onSubmit={saveProfile} className="mt-4 space-y-3">
            <LabeledInput label="Name" value={name} onChange={setName} placeholder="Jane Hero" testId="input-name" />
            <LabeledInput label="Email" value={email} onChange={setEmail} type="email" testId="input-email" />
            <LabeledInput label="Phone" value={phone} onChange={setPhone} placeholder="+1 415 555 1234" testId="input-phone" hint="Used by your bank to recognize you. Country code (+1, +44, etc.) is required." inputMode="tel" />
            <LabeledInput label="Address" value={address} onChange={setAddress} placeholder="123 Hero Lane" testId="input-address" />
            <Button
              type="submit"
              disabled={saving}
              className="w-full h-11 rounded-xl bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] font-black uppercase tracking-wider"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="save-profile"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-1.5" /> Save Profile</>)}
            </Button>
          </form>

          {/* Danger zone — Play Store / GDPR compliance */}
          <div className="mt-6 pt-5 border-t border-[#1B4238]" data-testid="danger-zone">
            <div className="flex items-start gap-2.5 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-[2px] flex-shrink-0" strokeWidth={2.4} />
              <div>
                <p className="text-[13px] font-bold text-red-300 leading-tight">Delete account</p>
                <p className="text-[11px] text-[#7B948B] leading-snug mt-0.5">
                  Permanently removes your profile, subscriptions, linked banks, vault history, and team memberships. This can&apos;t be undone.
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setDeleteConfirmText(""); setDeleteDialogOpen(true); }}
              className="w-full h-10 rounded-xl border border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200 font-bold uppercase tracking-wider text-xs mt-2"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="delete-account-trigger"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.6} /> Delete my account
            </Button>
          </div>
        </section>

        {/* Security */}
        <section className="ht-card rounded-2xl p-5" data-testid="section-security">
          <SectionHeader icon={Lock} title="Change Password" />
          <form onSubmit={changePw} className="mt-4 space-y-3">
            <LabeledInput label="Current password" value={currentPw} onChange={setCurrentPw} type="password" testId="input-current-pw" />
            <LabeledInput label="New password (8+ chars)" value={newPw} onChange={setNewPw} type="password" testId="input-new-pw" />
            <Button
              type="submit"
              disabled={saving || !newPw}
              className="w-full h-11 rounded-xl bg-[#F5D849] hover:bg-[#E3B800] text-[#0B1E1A] font-black uppercase tracking-wider"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="save-password"
            >
              Update Password
            </Button>
          </form>
        </section>

        {/* MFA / 2FA */}
        <section className="ht-card rounded-2xl p-5" data-testid="section-mfa">
          <SectionHeader icon={ShieldCheck} title="Two-Factor Authentication" />
          <div className="mt-4">
            <MfaSection enabled={!!account?.mfa_enabled} onChanged={load} />
          </div>
        </section>

        {/* Banks */}
        <section className="ht-card rounded-2xl p-5" data-testid="section-banks">
          <div className="flex items-center justify-between mb-3">
            <SectionHeader icon={Landmark} title="Linked Banks" />
            {plaidStatus.configured && (
              <span className="text-[10px] font-black uppercase tracking-wider text-[#94B0A6]">
                {plaidStatus.item_count}/{plaidStatus.max_banks} used
              </span>
            )}
          </div>
          {(() => {
            // Group flat account list by item_id so each bank shows once with
            // its accounts nested under it.
            const grouped = new Map();
            for (const acc of banks) {
              const key = acc.item_id || acc.name || "unknown";
              if (!grouped.has(key)) {
                grouped.set(key, {
                  item_id: acc.item_id,
                  institution_name: acc.institution_name || acc.name || "Linked Bank",
                  accounts: [],
                });
              }
              grouped.get(key).accounts.push(acc);
            }
            const items = Array.from(grouped.values());
            if (items.length === 0) {
              return (
                <div className="mt-2 text-center py-6 border border-dashed border-[#1B4238] rounded-xl">
                  <Landmark className="w-8 h-8 text-[#5F7E76] mx-auto" strokeWidth={2.2} />
                  <p className="mt-2 text-sm text-[#C7DAD3]">No banks linked yet.</p>
                  <p className="text-[11px] text-[#94B0A6]">Link one to auto-detect subscriptions.</p>
                </div>
              );
            }
            return (
              <ul className="divide-y divide-[#1B4238]" data-testid="bank-items-list">
                {items.map((bank) => {
                  const isEditing = editingBankId === bank.item_id;
                  const nickname = bank.accounts[0]?.nickname || null;
                  const displayLabel = nickname || bank.institution_name;
                  return (
                  <li
                    key={bank.item_id || bank.institution_name}
                    className="py-3"
                    data-testid={`bank-${bank.item_id || bank.institution_name}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex items-center gap-3 flex-1">
                        <Plug className="w-4 h-4 text-[#22E07A] flex-shrink-0" strokeWidth={2.4} />
                        <div className="min-w-0 flex-1">
                          {isEditing ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                value={editingNickname}
                                onChange={(e) => setEditingNickname(e.target.value)}
                                placeholder={bank.institution_name}
                                maxLength={48}
                                className="h-8 text-sm bg-[#0F2E27] border-[#1B4238] text-white"
                                data-testid={`bank-nickname-input-${bank.item_id}`}
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={() => saveNickname(bank.item_id)}
                                disabled={savingNickname}
                                className="p-1.5 rounded-md bg-[#22E07A] text-[#05160D] disabled:opacity-60"
                                data-testid={`bank-nickname-save-${bank.item_id}`}
                                aria-label="Save nickname"
                              >
                                {savingNickname ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                              </button>
                              <button
                                type="button"
                                onClick={cancelEditNickname}
                                className="p-1.5 rounded-md text-[#94B0A6] hover:text-white"
                                data-testid={`bank-nickname-cancel-${bank.item_id}`}
                                aria-label="Cancel edit"
                              >
                                <X className="w-3.5 h-3.5" strokeWidth={3} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-1.5">
                                <div className="text-sm font-bold text-white truncate">{displayLabel}</div>
                                <button
                                  type="button"
                                  onClick={() => startEditNickname(bank.item_id, nickname)}
                                  className="text-[#94B0A6] hover:text-white flex-shrink-0"
                                  data-testid={`bank-edit-nickname-${bank.item_id}`}
                                  aria-label="Edit nickname"
                                >
                                  <Pencil className="w-3 h-3" strokeWidth={2.6} />
                                </button>
                              </div>
                              <div className="text-[11px] text-[#94B0A6] uppercase tracking-wider">
                                {nickname ? `${bank.institution_name} · ` : ""}
                                {bank.accounts.length} account{bank.accounts.length === 1 ? "" : "s"}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {!isEditing && (
                        <button
                          type="button"
                          onClick={() => disconnectBank(bank.item_id)}
                          className="text-[11px] font-extrabold uppercase tracking-wider text-[#FF3B3B] hover:text-red-300 px-2 py-1 flex-shrink-0"
                          data-testid={`disconnect-bank-${bank.item_id || bank.institution_name}`}
                        >
                          Disconnect
                        </button>
                      )}
                    </div>
                    {bank.accounts.length > 0 && !isEditing && (
                      <ul className="mt-2 pl-7 space-y-1">
                        {bank.accounts.map((a) => (
                          <li
                            key={a.account_id || a.name}
                            className="flex items-center justify-between text-[11px] text-[#94B0A6]"
                          >
                            <span className="truncate">
                              {a.name}
                              {a.mask ? ` •••${a.mask}` : ""}
                              {a.subtype ? ` · ${a.subtype}` : ""}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                  );
                })}
              </ul>
            );
          })()}
          {plaidStatus.configured && (
            <div className="mt-4 pt-4 border-t border-[#1B4238]" data-testid="account-connect-bank">
              <ConnectBankButton
                status={plaidStatus}
                onLinked={reloadBanks}
                onSynced={reloadBanks}
                onDisconnect={reloadBanks}
              />
            </div>
          )}
        </section>

        {/* Notifications */}
        <section className="ht-card rounded-2xl p-5" data-testid="section-notifications">
          <SectionHeader icon={Bell} title="Notifications" />
          <ul className="mt-3 divide-y divide-[#1B4238]">
            {Object.entries(NOTIF_LABELS).map(([key, label]) => {
              const on = !!account?.notification_prefs?.[key];
              return (
                <li key={key} className="py-3 flex items-center justify-between gap-3" data-testid={`notif-${key}`}>
                  <span className="text-sm text-[#F4FAF7]">{label}</span>
                  <Switch
                    checked={on}
                    onCheckedChange={(v) => toggleNotif(key, v)}
                    data-testid={`notif-switch-${key}`}
                  />
                </li>
              );
            })}
          </ul>
        </section>

        {/* App Lock */}
        <section className="ht-card rounded-2xl p-5" data-testid="section-app-lock">
          <SectionHeader icon={Grid3x3} title="App Lock (Pattern)" />
          <p className="mt-3 text-sm text-[#C7DAD3]">
            Protect your financial data with a 9-dot pattern — required each time the app opens.
          </p>
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[12px] font-extrabold uppercase tracking-wider">
              {account?.app_lock_enabled ? (
                <>
                  <Check className="w-4 h-4 text-[#22E07A]" strokeWidth={3} />
                  <span className="text-[#22E07A]">Enabled</span>
                </>
              ) : (
                <>
                  <X className="w-4 h-4 text-[#94B0A6]" strokeWidth={3} />
                  <span className="text-[#94B0A6]">Disabled</span>
                </>
              )}
            </div>
            <Button
              type="button"
              onClick={openLockFlow}
              className={`h-10 rounded-xl px-4 font-black uppercase tracking-wider text-xs ${
                account?.app_lock_enabled
                  ? "bg-[#0E2924] text-[#FF3B3B] border-2 border-[#FF3B3B]"
                  : "bg-[#22E07A] text-[#05160D]"
              }`}
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="app-lock-toggle"
            >
              {account?.app_lock_enabled ? "Disable" : "Set up pattern"}
            </Button>
          </div>
        </section>
      </div>

      <AlertDialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <AlertDialogContent className="hero-theme bg-[#0E2924] border-[#1B4238]" data-testid="pattern-lock-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white text-center">
              {lockStage === "new" && "Draw a new pattern"}
              {lockStage === "confirm" && "Draw it again to confirm"}
              {lockStage === "disable" && "Draw pattern to disable lock"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#C7DAD3] text-center text-xs">
              Connect at least 4 dots. Your pattern is stored encrypted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-center py-4">
            <PatternLock size={260} onComplete={handlePatternComplete} error={patternError} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-[#13362E] text-white border-[#1B4238]"
              onClick={() => setLockDialogOpen(false)}
              data-testid="pattern-cancel"
            >
              Cancel
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="hero-theme bg-[#0E2924] border-red-500/30" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-300 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" strokeWidth={2.4} /> Delete account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#C7DAD3] text-sm leading-relaxed pt-2">
              This permanently removes your account, all your subscriptions, linked banks, vault history, login activity, and team memberships. <strong className="text-white">It cannot be undone.</strong>
              <br /><br />
              Type <code className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-200 font-bold tracking-wider">DELETE</code> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE"
            disabled={deleting}
            autoComplete="off"
            className="mt-2 h-10 bg-[#0A2320] border-[#1B4238] text-white tracking-wider"
            data-testid="delete-confirm-input"
          />
          <AlertDialogFooter className="mt-3">
            <AlertDialogCancel
              disabled={deleting}
              className="bg-transparent border-[#1B4238] text-[#C7DAD3] hover:bg-[#11413B]/60 hover:text-white"
              data-testid="delete-account-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting || deleteConfirmText.trim().toUpperCase() !== "DELETE"}
              className="bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50"
              data-testid="delete-account-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Trash2 className="w-4 h-4 mr-1.5" /> Delete forever</>)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-5 h-5 text-[#F5D849]" strokeWidth={2.4} />
      <h2 className="text-lg font-black uppercase tracking-wider text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
        {title}
      </h2>
    </div>
  );
}

function LabeledInput({ label, value, onChange, type = "text", placeholder, testId, hint, inputMode }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wider text-[#94B0A6]">{label}</label>
      <Input
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 h-10 bg-[#0A2320] border-[#1B4238] text-white"
        data-testid={testId}
      />
      {hint && (
        <p className="mt-1 text-[11px] text-[#7B948B] leading-snug">{hint}</p>
      )}
    </div>
  );
}
